import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, FileCheck, Clock, Upload, Download, Trash2, Pencil, Plus } from "lucide-react";
import { readFileAsBase64, PreviewButton } from "@/components/FilePreview";
import { fmtDate } from "@/utils/format";
import PageHeader from "@/components/PageHeader";

const PERSONNEL_DOCS = [
  { key: "isg_training", label: "İSG Eğitimi" },
  { key: "isg_certificate", label: "İSG Sertifikası" },
  { key: "health_report", label: "Sağlık Raporu" },
  { key: "myk_certificate", label: "MYK Belgesi" },
  { key: "kkd_form", label: "KKD Hizmet Formu" },
  { key: "first_aid_certificate", label: "İlk Yardım Sertifikası" },
];

const COMPANY_DOCS = [
  { key: "risk_assessment", label: "Risk Değerlendirmesi" },
  { key: "emergency_plan", label: "Acil Durum Eylem Planı" },
  { key: "fire_drill", label: "Yangın Tatbikatı" },
  { key: "periodic_inspections", label: "Periyodik Kontroller" },
  { key: "employee_representative", label: "Çalışan Temsilcisi" },
  { key: "support_personnel", label: "Destek Elemanları" },
];

function StatusChip({ status }) {
  const map = {
    valid:          { cls: "bg-[#00A650] text-white border-[#00A650]", label: "Geçerli" },
    expiring_soon:  { cls: "bg-[#FF9500] text-white border-[#FF9500]", label: "Yakında Dolacak" },
    expired:        { cls: "bg-[#FF3B30] text-white border-[#FF3B30]", label: "Süresi Dolmuş" },
    missing:        { cls: "bg-neutral-200 text-neutral-700 border-neutral-400", label: "Eksik" },
  };
  const m = map[status] || map.missing;
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase border-2 ${m.cls}`} data-testid={`hse-status-${status}`}>{m.label}</span>;
}

function DocumentForm({ doc, onSave, onDelete, onDownload, fileUrl, extraFields = [], hideActions = false }) {
  const [issue, setIssue] = useState(doc.issue_date || "");
  const [expiry, setExpiry] = useState(doc.expiry_date || "");
  const [responsible, setResponsible] = useState(doc.responsible || "");
  const [note, setNote] = useState(doc.note || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setIssue(doc.issue_date || "");
    setExpiry(doc.expiry_date || "");
    setResponsible(doc.responsible || "");
    setNote(doc.note || "");
    setFile(null);
  }, [doc.doc_type, doc.updated_at]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        issue_date: issue, expiry_date: expiry, note,
        responsible: extraFields.includes("responsible") ? responsible : "",
      };
      if (file) {
        const f = await readFileAsBase64(file);
        payload.file_b64 = f.b64;
        payload.file_name = f.name;
        payload.content_type = f.content_type;
      }
      await onSave(payload);
      toast.success("Kaydedildi");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      {doc.status === "expired" && (
        <div className="flex items-center gap-2 border-2 border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30] text-xs font-mono p-2" data-testid="hse-expired-warning">
          <AlertTriangle size={14} /> Belgenin geçerlilik tarihi geçmiş. Lütfen yenileyin.
        </div>
      )}
      {doc.status === "expiring_soon" && (
        <div className="flex items-center gap-2 border-2 border-[#FF9500] bg-[#FF9500]/10 text-[#FF9500] text-xs font-mono p-2">
          <Clock size={14} /> Geçerlilik tarihi 30 gün içinde doluyor.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs font-mono uppercase">Açıklama</Label>
          <Input value={note} maxLength={120} onChange={(e) => setNote(e.target.value)} placeholder="Ör. 2026 yıllık tatbikat"
            className="rounded-none border-2 border-[#09090B]" data-testid={`hse-${doc.doc_type}-note`} />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">Düzenleme Tarihi</Label>
          <Input type="date" value={issue} onChange={(e) => setIssue(e.target.value)}
            className="rounded-none border-2 border-[#09090B] font-mono" data-testid={`hse-${doc.doc_type}-issue`} />
        </div>
        {doc.doc_type !== "health_report" && (
        <div>
          <Label className="text-xs font-mono uppercase">
            {extraFields.includes("responsible") ? "Yenileme / Geçerlilik Tarihi" : "Geçerlilik Tarihi"}
          </Label>
          <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
            className="rounded-none border-2 border-[#09090B] font-mono" data-testid={`hse-${doc.doc_type}-expiry`} />
        </div>
        )}
        {extraFields.includes("responsible") && (
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase">Sorumlu Kişi</Label>
            <Input value={responsible} onChange={(e) => setResponsible(e.target.value)}
              className="rounded-none border-2 border-[#09090B]" data-testid={`hse-${doc.doc_type}-responsible`} />
          </div>
        )}
        <div className="md:col-span-2">
          <Label className="text-xs font-mono uppercase">Dosya {doc.has_file && <span className="ml-2 text-[10px] text-neutral-500">({doc.file_name})</span>}</Label>
          <div className="flex items-center gap-2">
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="rounded-none border-2 border-[#09090B]" data-testid={`hse-${doc.doc_type}-file`} />
            {doc.has_file && !hideActions && <PreviewButton doc={doc} fileUrl={fileUrl} testid={`hse-${doc.doc_type}-preview`} />}
            {doc.has_file && !hideActions && (
              <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onDownload} data-testid={`hse-${doc.doc_type}-download`}>
                <Download size={14} />
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-2">
        <StatusChip status={doc.status} />
        <div className="flex-1" />
        {doc.has_file && !hideActions && (
          <Button variant="outline" className="rounded-none border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
            onClick={onDelete} data-testid={`hse-${doc.doc_type}-delete`}>
            <Trash2 size={14} className="mr-1" /> Sil
          </Button>
        )}
        <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" disabled={busy}
          onClick={save} data-testid={`hse-${doc.doc_type}-save`}>
          <Upload size={14} className="mr-1" /> {busy ? "Kaydediliyor…" : "Kaydet"}
        </Button>
      </div>
    </div>
  );
}

function PersonnelDocsDialog({ pid, name, open, onOpenChange, onChanged }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/hse/personnel/${pid}/documents`);
      setDocs(data.documents || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, pid]);

  const save = async (docType, payload) => {
    await api.put(`/hse/personnel/${pid}/documents/${docType}`, payload);
    await load();
    onChanged?.();
  };
  const del = async (docType) => {
    if (!window.confirm("Bu belgeyi silmek istiyor musunuz?")) return;
    await api.delete(`/hse/personnel/${pid}/documents/${docType}`);
    await load();
    onChanged?.();
    toast.success("Silindi");
  };
  const download = async (docType, filename) => {
    const res = await api.get(`/hse/personnel/${pid}/documents/${docType}/file`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename || "belge";
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-[#09090B] bg-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="hse-personnel-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
            <ShieldCheck size={22} /> {name}
            <span className="text-sm font-mono text-neutral-500 ml-2">· İSG Belgeleri</span>
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-neutral-500 text-center py-10">Yükleniyor…</div>
        ) : (
          <Tabs defaultValue={PERSONNEL_DOCS[0].key} className="w-full">
            <TabsList className="rounded-none border-2 border-[#09090B] bg-white h-auto p-0 flex-wrap">
              {PERSONNEL_DOCS.map((d) => {
                const found = docs.find((x) => x.doc_type === d.key);
                return (
                  <TabsTrigger key={d.key} value={d.key}
                    className="rounded-none px-3 py-2 data-[state=active]:bg-[#09090B] data-[state=active]:text-white font-mono uppercase text-[11px]"
                    data-testid={`hse-tab-${d.key}`}>
                    {d.label}
                    {found?.status === "expired" && <AlertTriangle size={10} className="ml-1 text-[#FF3B30]" />}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {PERSONNEL_DOCS.map((d) => {
              const doc = docs.find((x) => x.doc_type === d.key) || { doc_type: d.key, status: "missing" };
              return (
                <TabsContent key={d.key} value={d.key} className="pt-4">
                  <DocumentForm
                    doc={doc}
                    onSave={(payload) => save(d.key, payload)}
                    onDelete={() => del(d.key)}
                    onDownload={() => download(d.key, doc.file_name)}
                    fileUrl={`/hse/personnel/${pid}/documents/${d.key}/file`}
                  />
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CompanyRecordRow({ rec, onEdit, onDelete, onDownload }) {
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-b border-neutral-200 last:border-b-0" data-testid={`hse-company-record-${rec.id}`}>
      <div className="flex-1 min-w-[180px] text-xs font-mono space-y-0.5">
        {rec.note && <div className="text-sm font-sans font-semibold break-words" data-testid={`hse-record-${rec.id}-note`}>{rec.note}</div>}
        <div>Düzenleme: {fmtDate(rec.issue_date) || "-"} · Geçerlilik: {fmtDate(rec.expiry_date) || "-"}</div>
        <div className="text-neutral-500 truncate">Sorumlu: {rec.responsible || "-"} · {rec.file_name || "Dosya yok"}</div>
      </div>
      <StatusChip status={rec.status} />
      {rec.has_file && <PreviewButton doc={rec} fileUrl={`/hse/company/records/${rec.id}/file`} testid={`hse-rec-${rec.id}-preview`} />}
      {rec.has_file && (
        <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onDownload} data-testid={`hse-record-${rec.id}-download`}>
          <Download size={14} />
        </Button>
      )}
      <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onEdit} data-testid={`hse-record-${rec.id}-edit`}>
        <Pencil size={14} />
      </Button>
      <Button variant="outline" className="rounded-none border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
        onClick={onDelete} data-testid={`hse-record-${rec.id}-delete`}>
        <Trash2 size={14} />
      </Button>
    </div>
  );
}

function CompanyRecordDialog({ editing, onClose, onSave }) {
  const doc = editing?.record || { doc_type: editing?.docType || "new", status: "missing" };
  return (
    <Dialog open={!!editing} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-[#09090B] bg-white max-w-2xl" data-testid="hse-company-record-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight">
            {editing?.label} · {editing?.record ? "Kaydı Düzenle" : "Yeni Kayıt"}
          </DialogTitle>
        </DialogHeader>
        {editing && (
          <DocumentForm doc={{ ...doc, doc_type: "record" }} onSave={onSave} extraFields={["responsible"]} hideActions />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CompanyPanel() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hse/company/documents");
      setDocs(data.documents || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (editing.record) await api.put(`/hse/company/records/${editing.record.id}`, payload);
    else await api.post(`/hse/company/documents/${editing.docType}`, payload);
    setEditing(null);
    await load();
  };
  const del = async (rid) => {
    if (!window.confirm("Bu kaydı silmek istiyor musunuz?")) return;
    await api.delete(`/hse/company/records/${rid}`);
    await load();
    toast.success("Silindi");
  };
  const download = async (rid, filename) => {
    const res = await api.get(`/hse/company/records/${rid}/file`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename || "belge";
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
  };

  if (loading && !docs.length) return <div className="text-sm text-neutral-500 text-center py-10">Yükleniyor…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="hse-company-grid">
      <CompanyRecordDialog editing={editing} onClose={() => setEditing(null)} onSave={save} />
      {COMPANY_DOCS.map((d) => {
        const doc = docs.find((x) => x.doc_type === d.key) || { doc_type: d.key, status: "missing", records: [] };
        return (
          <div key={d.key} className="bg-white border-2 border-[#09090B] p-4 space-y-3" data-testid={`hse-company-card-${d.key}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileCheck size={16} />
                <div className="font-heading text-lg tracking-tight">{d.label}</div>
              </div>
              <StatusChip status={doc.status} />
            </div>
            <div className="border-t-2 border-[#09090B]" data-testid={`hse-company-records-${d.key}`}>
              {doc.records.length === 0 && <div className="text-xs font-mono text-neutral-500 py-3">Henüz kayıt yok</div>}
              {doc.records.map((rec) => (
                <CompanyRecordRow key={rec.id} rec={rec}
                  onEdit={() => setEditing({ docType: d.key, label: d.label, record: rec })}
                  onDelete={() => del(rec.id)}
                  onDownload={() => download(rec.id, rec.file_name)} />
              ))}
            </div>
            <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={() => setEditing({ docType: d.key, label: d.label })}
              data-testid={`hse-company-add-${d.key}`}>
              <Plus size={14} className="mr-1" /> Yeni Ekle
            </Button>
          </div>
        );
      })}
    </div>
  );
}

export default function HSE() {
  const [overview, setOverview] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPid, setSelectedPid] = useState(null);
  const [selectedName, setSelectedName] = useState("");
  const [filterActive, setFilterActive] = useState("all"); // all | active | passive

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/hse/personnel-overview?_ts=${Date.now()}`);
      setOverview(data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filterActive === "active") return overview.filter((p) => p.active);
    if (filterActive === "passive") return overview.filter((p) => !p.active);
    return overview;
  }, [overview, filterActive]);

  return (
    <div className="space-y-6" data-testid="hse-page">
      <PageHeader
        kicker="İş Sağlığı ve Güvenliği"
        title="İSG"
        onRefresh={load}
        refreshing={refreshing}
      />

      <Tabs defaultValue="personnel" className="w-full">
        <TabsList className="rounded-none border-2 border-[#09090B] bg-white h-auto p-0">
          <TabsTrigger value="personnel" className="rounded-none px-6 py-3 data-[state=active]:bg-[#09090B] data-[state=active]:text-white font-mono uppercase text-xs"
            data-testid="hse-tab-personnel">
            Personel ({overview.length})
          </TabsTrigger>
          <TabsTrigger value="company" className="rounded-none px-6 py-3 data-[state=active]:bg-[#09090B] data-[state=active]:text-white font-mono uppercase text-xs"
            data-testid="hse-tab-company">
            Şirket
          </TabsTrigger>
        </TabsList>

        <TabsContent value="personnel" className="pt-4 space-y-3">
          <div className="flex items-center gap-2">
            {["all", "active", "passive"].map((v) => (
              <Button
                key={v}
                variant={filterActive === v ? "default" : "outline"}
                className={`rounded-none border-2 border-[#09090B] ${filterActive === v ? "bg-[#09090B] text-white" : ""}`}
                onClick={() => setFilterActive(v)}
                data-testid={`hse-filter-${v}`}
              >
                {v === "all" ? "Tümü" : v === "active" ? "Aktif" : "Pasif"}
              </Button>
            ))}
          </div>
          <div className="bg-white border-2 border-[#09090B]">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
                  <TableHead className="font-mono uppercase text-xs">Personel</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Durum</TableHead>
                  {PERSONNEL_DOCS.map((d) => (
                    <TableHead key={d.key} className="font-mono uppercase text-[10px] whitespace-nowrap">{d.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-neutral-50"
                    onClick={() => { setSelectedPid(p.id); setSelectedName(p.name); }}
                    data-testid={`hse-personnel-row-${p.id}`}>
                    <TableCell className="font-semibold">
                      <div>{p.name}</div>
                      {p.role_title && <div className="text-[10px] font-mono text-neutral-500">{p.role_title}</div>}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase border-2 ${p.active ? "bg-[#00A650] text-white border-[#00A650]" : "bg-neutral-200 text-neutral-700 border-neutral-400"}`}>
                        {p.active ? "Aktif" : "Pasif"}
                      </span>
                    </TableCell>
                    {PERSONNEL_DOCS.map((d) => (
                      <TableCell key={d.key}>
                        <StatusChip status={p.docs?.[d.key]?.status || "missing"} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2 + PERSONNEL_DOCS.length} className="text-center py-8 text-neutral-500">
                      Personel bulunamadı
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="company" className="pt-4">
          <CompanyPanel />
        </TabsContent>
      </Tabs>

      <PersonnelDocsDialog
        pid={selectedPid}
        name={selectedName}
        open={!!selectedPid}
        onOpenChange={(v) => { if (!v) setSelectedPid(null); }}
        onChanged={load}
      />
    </div>
  );
}
